import {
    TypographyFamilyBodyFontFamily,
    TypographyFamilyBodyFontWeightBold,
    TypographyFamilyBodyFontWeightDefault,
    TypographyFamilyBodyFontWeightSemiBold,
    TypographyFamilyHeadingFontFamily,
    TypographyFamilyHeadingFontWeightDefault,
    TypographyFamilyHeroFontFamily,
    TypographyFamilyHeroFontWeightDefault,
    TypographyFamilyHeroTextTransform,
    TypographyFamilyPriceFontFamily,
    TypographyFamilyPriceFontWeightDefault,
} from '../design-tokens/travel-republic-tokens';

import { HoneycombStyleSystemProps } from '../theming';
import { HoneycombThemeOverrides } from '../theming/types/HoneycombTheme';

const PalettePrimaryBase = '#1B2870'; // Navy
const PalettePrimaryDarkest = '#101843'; // Darkest Navy
const PaletteSecondaryBase = '#FD5F3F'; // Coral
const PaletteTertiaryBase = '#108193'; // Teal
const PaletteAccentPrimaryBase = '#8CE7FF'; // Sky Blue
const PaletteAccentSecondaryBase = '#FF99F5'; // Pink
const PaletteAccentTertiaryBase = '#E3FB68'; // Lime

const PaletteBackgroundBody = '#F7F4F3';
const PaletteBorder = '#E4DDD7';
const PaletteWhite = '#FFFFFF';

export const TRAVEL_REPUBLIC_THEME_OVERRIDES: HoneycombThemeOverrides = {
    palette: {
        primary: {
            baseTint: '500',
            contrastText: PaletteWhite,
            tint: {
                '100': '#D1D4E2',
                '200': '#A4A9C6',
                '300': '#767EA9',
                '400': '#49538D',
                '500': PalettePrimaryBase,
                '600': '#16205A',
                '700': PalettePrimaryDarkest,
            },
        },
        secondary: {
            baseTint: '500',
            contrastText: PaletteWhite,
            tint: {
                '100': '#FFDFD9',
                '200': '#FEBFB2',
                '300': '#FE9F8C',
                '400': '#FD7F65',
                '500': PaletteSecondaryBase,
                '600': '#E45639',
                '700': '#B1432C',
            },
        },
        tertiary: {
            base: PaletteTertiaryBase,
            baseTint: '500',
            contrastText: PaletteWhite,
        },
        accent: {
            base: PaletteAccentPrimaryBase,
            baseTint: '500',
        },
        accentPrimary: {
            base: PaletteAccentPrimaryBase,
            baseTint: '500',
        },
        accentSecondary: {
            base: PaletteAccentSecondaryBase,
            baseTint: '500',
        },
        accentTertiary: {
            base: PaletteAccentTertiaryBase,
            baseTint: '500',
        },
        neutral: {
            contrastText: '#000000',
            base: PaletteBackgroundBody,
            baseTint: '100',
        },
        text: {
            body: PalettePrimaryDarkest,
            heading: PalettePrimaryBase,
            price: PalettePrimaryBase,
            link: PalettePrimaryBase,
        },
        background: {
            body: PaletteBackgroundBody,
            offset: PaletteBackgroundBody,
            content: PaletteWhite,
        },
        starRating: PaletteSecondaryBase,
        divider: PaletteBackgroundBody,
        border: PaletteBorder,
    },
    typography: {
        family: {
            body: {
                fontFamily: TypographyFamilyBodyFontFamily,
                fontWeight: {
                    default: TypographyFamilyBodyFontWeightDefault,
                    regular: TypographyFamilyBodyFontWeightDefault,
                    semiBold: TypographyFamilyBodyFontWeightSemiBold,
                    bold: TypographyFamilyBodyFontWeightBold,
                },
            },
            caption: {
                fontFamily: TypographyFamilyBodyFontFamily,
                fontWeight: {
                    default: TypographyFamilyBodyFontWeightDefault,
                    regular: TypographyFamilyBodyFontWeightDefault,
                    semiBold: TypographyFamilyBodyFontWeightSemiBold,
                    bold: TypographyFamilyBodyFontWeightBold,
                },
            },
            heading: {
                fontFamily: TypographyFamilyHeadingFontFamily,
                fontWeight: {
                    default: TypographyFamilyHeadingFontWeightDefault,
                },
            },
            hero: {
                fontFamily: TypographyFamilyHeroFontFamily,
                fontWeight: {
                    default: TypographyFamilyHeroFontWeightDefault,
                },
                textTransform: TypographyFamilyHeroTextTransform,
            },
            price: {
                fontFamily: TypographyFamilyPriceFontFamily,
                fontWeight: {
                    default: TypographyFamilyPriceFontWeightDefault,
                },
            },
        },
        variant: {
            heading: {
                l: {
                    xs: {
                        fontSize: '1.75rem', // 28px
                        lineHeight: '2.5rem', // 40px
                    },
                    m: {
                        fontSize: '2rem', // 32px
                        lineHeight: '2.75rem', // 44px
                    },
                    l: {
                        fontSize: '2rem', // 32px
                        lineHeight: '2.75rem', // 44px
                    },
                },
                xl: {
                    xs: {
                        fontSize: '2rem', // 32px
                        lineHeight: '2.5rem', // 40px
                    },
                    m: {
                        fontSize: '2rem', // 36px
                        lineHeight: '2rem', // 44px
                    },
                    l: {
                        fontSize: '2.25rem', // 36px
                        lineHeight: '2.75rem', // 44px
                    },
                },
            },
            hero: {
                xs: {
                    xs: {
                        fontSize: '1.25rem', // 20px
                        lineHeight: 0.9,
                    },
                    m: {
                        fontSize: '1.25rem', // 20px
                        lineHeight: 0.9,
                    },
                    l: {
                        fontSize: '1.25rem', // 20px
                        lineHeight: 0.9,
                    },
                },
                s: {
                    xs: {
                        fontSize: '1.5rem', // 24px
                        lineHeight: 0.9,
                    },
                    m: {
                        fontSize: '1.5rem', // 24px
                        lineHeight: 0.9,
                    },
                    l: {
                        fontSize: '1.5rem', // 24px
                        lineHeight: 0.9,
                    },
                },
                m: {
                    xs: {
                        fontSize: '2rem', // 32px
                        lineHeight: 0.9,
                    },
                    m: {
                        fontSize: '2rem', // 32px
                        lineHeight: 0.9,
                    },
                    l: {
                        fontSize: '2rem', // 32px
                        lineHeight: 0.9,
                    },
                },
                l: {
                    xs: {
                        fontSize: '2rem', // 32px
                        lineHeight: 0.9,
                    },
                    m: {
                        fontSize: '2.25rem', // 36px
                        lineHeight: 0.9,
                    },
                    l: {
                        fontSize: '2.5rem', // 40px
                        lineHeight: 0.9,
                    },
                },
                xl: {
                    xs: {
                        fontSize: '2.25rem', // 36px
                        lineHeight: 0.9,
                    },
                    m: {
                        fontSize: '3rem', // 48px
                        lineHeight: 0.9,
                    },
                    l: {
                        fontSize: '3.5rem', // 56px
                        lineHeight: 0.9,
                    },
                },
                '2xl': {
                    xs: {
                        fontSize: '2.5rem', // 40px
                        lineHeight: 0.9,
                    },
                    m: {
                        fontSize: '3.5rem', // 56px
                        lineHeight: 0.9,
                    },
                    l: {
                        fontSize: '4rem', // 64px
                        lineHeight: 0.9,
                    },
                },
                '3xl': {
                    xs: {
                        fontSize: '2.75rem', // 44px
                        lineHeight: 0.9,
                    },
                    m: {
                        fontSize: '4rem', // 64px
                        lineHeight: 0.9,
                    },
                    l: {
                        fontSize: '6rem', // 96px
                        lineHeight: 0.9,
                    },
                },
            },
            price: {
                m: {
                    xs: {
                        fontSize: '1.5rem', // 24px
                        lineHeight: 0.9,
                    },
                    m: {
                        fontSize: '1.75rem', // 28px
                        lineHeight: 0.9,
                    },
                    l: {
                        fontSize: '2rem', // 32px
                        lineHeight: 0.9,
                    },
                },
                l: {
                    xs: {
                        fontSize: '2rem', // 32px
                        lineHeight: 0.9,
                    },
                    m: {
                        fontSize: '2.25rem', // 36px
                        lineHeight: 0.9,
                    },
                    l: {
                        fontSize: '2.5rem', // 40px
                        lineHeight: 0.9,
                    },
                },
                xl: {
                    xs: {
                        fontSize: '2.5rem', // 40px
                        lineHeight: 0.9,
                    },
                    m: {
                        fontSize: '3rem', // 48px
                        lineHeight: 0.9,
                    },
                    l: {
                        fontSize: '3.5rem', // 56px
                        lineHeight: 0.9,
                    },
                },
            },
        },
    },
    components: {
        defaultProps: {
            Paper: {
                cornerStyle: 'rounded',
                border: 0,
            },
            Button: {
                cornerStyle: 'rounded',
            },
            IconButton: {
                shape: 'rounded',
            },
            AutocompleterCategory: {
                cornerStyle: 'rounded',
            },
            Accordion: {
                bordercolor: PalettePrimaryBase,
            },
            Dialog: {
                cornerStyle: 'rounded',
            },
            Popup: {
                borderWidth: 1,
            },
            Gallery: {
                cornerStyle: 'rounded',
            },
            Notification: {
                cornerStyle: 'rounded',
            },
            Option: {
                cornerStyle: 'rounded',
            },
            ProgressBar: {
                color: 'secondary.base',
            },
        },
        themes: {
            Button: {
                colors: {
                    callToAction: {
                        background: 'secondary.base',
                        border: 'secondary.base',
                        textFilled: 'common.white',
                        textOutlined: 'secondary.base',
                    },
                },
                // All sizes now have 10px radius
                size: {
                    xs: {
                        radius: 10,
                    },
                    s: {
                        radius: 10,
                    },
                    m: {
                        radius: 10,
                    },
                    l: {
                        radius: 10,
                    },
                    xl: {
                        radius: 10,
                    },
                    '2xl': {
                        radius: 10,
                    },
                },
            },
            FieldBase: {
                // All sizes now have 10px radius
                size: {
                    s: {
                        radius: 10,
                    },
                    m: {
                        radius: 10,
                    },
                    l: {
                        radius: 10,
                    },
                },
            },
            Chip: {
                colors: {
                    tertiary: {
                        textFilled: 'common.white',
                        textOutlined: 'accent.base',
                    },
                },
            },
            Flag: {
                colors: {
                    primary: {
                        background: 'accentTertiary.base',
                        text: 'primary.base',
                    },
                    secondary: {
                        background: 'common.white',
                        text: 'common.black',
                    },
                    tertiary: {
                        background: 'common.white',
                        text: 'common.black',
                    },
                },
            },
            Stamp: {
                colors: {
                    primary: {
                        background: 'accentTertiary.base',
                        text: 'primary.base',
                    },
                    secondary: {
                        background: 'accentPrimary.base',
                        text: 'primary.base',
                    },
                    tertiary: {
                        background: 'accentSecondary.base',
                        text: 'primary.base',
                    },
                },
            },
            Score: {
                size: {
                    xs: {
                        fontSize: '0.625rem',
                        height: 'auto',
                        padding: '0.2em 0',
                    },
                    s: {
                        fontSize: '0.875rem',
                        height: 'auto',
                        padding: '0.4em 0',
                    },
                    m: {
                        fontSize: '1.25rem',
                        height: 'auto',
                        padding: '0.4em 0',
                    },
                    l: {
                        fontSize: '1.5rem',
                        height: 'auto',
                        padding: '0.4em 0',
                    },
                },
                typographyFamily: 'hero',
            },
            ModularPrice: {
                currencySizeCoefficient: 1,
                fractionSizeCoefficent: 1,
                currencyFontWeight: 'default',
                fractionFontWeight: 'default',
            },
            DatePickerDealButton: {
                colors: {
                    dealColor: 'secondary.base',
                    dealBackgroundColor: 'secondary.tint.100',
                },
            },
        },
    },
    // Smaller border radii
    radii: {
        none: 'none',
        '2xs': '2px',
        xs: '5px',
        s: '10px',
        m: '16px',
        l: '20px',
    },
};

export const TRAVEL_REPUBLIC_CUSTOM_STYLE_PROPS: {
    [key: string]: HoneycombStyleSystemProps;
} = {
    'deal-card-price': {
        c: 'secondary.base',
    },
    'price-block-card-price': {
        c: 'secondary.base',
    },
    'email-signup-heading-panel': {
        bg: { xs: 'primary.base', l: 'secondary.base' },
    },
    'email-signup-form-panel': {
        bg: 'primary.base',
    },
    'telephone-number': {
        c: 'secondary.base',
    },
    'error-page-hero-heading': {
        c: 'secondary.base',
    },
    'usp-bar': {
        bg: 'primary.base',
    },
};
